<script setup>
import { computed } from 'vue';
import { router } from '@inertiajs/vue3';
import {
    ComboboxAnchor,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxLabel,
    ComboboxRoot,
} from 'reka-ui';
import { IconArrowRight, IconLoader2, IconSearch, IconX } from '@tabler/icons-vue';
import { Dialog } from '@/components/ui/dialog';
import { useGlobalSearch } from '@/composables/useGlobalSearch';

const { open, query, results, loading, closeSearch } = useGlobalSearch();

const groups = [
    { key: 'clients', label: 'Klanten' },
    { key: 'vehicles', label: 'Voertuigen' },
    { key: 'invoices', label: 'Facturen' },
];

const activeGroups = computed(() => {
    return groups
        .map((group) => ({
            ...group,
            items: results.value[group.key] || [],
        }))
        .filter((group) => group.items.length > 0);
});

const hasResults = computed(() => activeGroups.value.length > 0);
const hasQuery = computed(() => query.value.length >= 2);

const onSelect = (result) => {
    if (!result) {
        return;
    }

    closeSearch();
    router.visit(result.url);
};

const dialogOpen = computed({
    get: () => open.value,
    set: (value) => {
        if (!value) {
            closeSearch();
        }
    },
});
</script>

<template>
    <Dialog
        v-model:open="dialogOpen"
        :closable="false"
        size="md"
        content-class="top-4 bottom-auto overflow-hidden p-0 sm:top-6 sm:translate-y-0 md:top-20 [&>div]:overflow-visible [&>div]:p-0"
    >
        <ComboboxRoot
            :model-value="null"
            class="overflow-hidden rounded-b-lg"
            ignore-filter
            @update:model-value="onSelect"
        >
            <ComboboxAnchor as-child>
                <div class="relative border-b border-gray-200 bg-white transition-colors focus-within:border-primary-500">
                    <IconSearch
                        class="pointer-events-none absolute left-4 top-3.5 size-5 text-gray-400"
                        aria-hidden="true"
                    />

                    <ComboboxInput
                        v-model="query"
                        class="h-12 w-full border-0 bg-transparent pl-11 pr-12 text-gray-900 outline-hidden placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                        placeholder="Zoeken..."
                        auto-focus
                    />

                    <IconLoader2
                        v-if="loading"
                        class="pointer-events-none absolute right-12 top-3.5 size-5 animate-spin text-gray-400"
                        aria-hidden="true"
                    />

                    <button
                        type="button"
                        class="absolute right-3 top-2.5 inline-flex size-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        aria-label="Sluiten"
                        @click="closeSearch"
                    >
                        <IconX
                            class="size-5"
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </ComboboxAnchor>

            <ComboboxContent
                v-if="hasQuery"
                class="max-h-80 scroll-py-2 overflow-y-auto bg-white p-2"
            >
                <ComboboxGroup
                    v-for="group in activeGroups"
                    :key="group.key"
                >
                    <ComboboxLabel class="px-4 pt-4 pb-2 text-xs font-semibold text-gray-500">
                        {{ group.label }}
                    </ComboboxLabel>

                    <ComboboxItem
                        v-for="item in group.items"
                        :key="`${group.key}-${item.id}`"
                        :value="item"
                        class="group flex cursor-pointer items-center justify-between rounded-md px-4 py-2.5 text-gray-900 outline-hidden transition-colors data-[highlighted]:bg-gray-100"
                    >
                        <div class="min-w-0">
                            <p class="truncate text-sm font-medium text-gray-900">
                                {{ item.title }}
                            </p>
                            <p
                                v-if="item.subtitle"
                                class="truncate text-sm text-gray-500"
                            >
                                {{ item.subtitle }}
                            </p>
                        </div>

                        <IconArrowRight
                            class="ml-3 size-4 shrink-0 text-primary opacity-0 transition-opacity group-data-[highlighted]:opacity-100"
                            aria-hidden="true"
                        />
                    </ComboboxItem>
                </ComboboxGroup>

                <ComboboxEmpty
                    v-if="!hasResults && !loading"
                    class="px-4 py-8 text-center text-sm text-gray-500"
                >
                    Geen resultaten gevonden.
                </ComboboxEmpty>
            </ComboboxContent>

            <div
                v-if="!hasQuery"
                class="border-t border-gray-100 px-4 py-8 text-center text-sm text-gray-500"
            >
                Zoek naar klanten, voertuigen of facturen...
            </div>
        </ComboboxRoot>
    </Dialog>
</template>
